import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CheckCheck, Star } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { SectionHead } from "./Brand";

const Bubble = ({ side, children, delay }) => (
  <div className={`chat-in flex ${side === "out" ? "justify-end" : "justify-start"}`} style={{ animationDelay: delay }}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${side === "out"
      ? "rounded-br-sm bg-gradient-to-br from-[#D4AF37] to-[#9A7B2C] text-black"
      : "rounded-bl-sm border border-[#D4AF37]/20 bg-white/[0.05] text-zinc-100"}`}>
      {children}
    </div>
  </div>
);

export const Testimonials = () => {
  const { t } = useLang();
  const items = t.testi.items;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = (d) => setI((p) => (p + d + items.length) % items.length);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setI((p) => (p + 1) % items.length), 5500);
    return () => clearInterval(id);
  }, [paused, items.length]);

  const cur = items[i];
  return (
    <section id="reviews" className="mx-auto max-w-7xl px-4 pb-32 pt-24 sm:px-6 md:pb-24 lg:px-8">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        <div>
          <SectionHead eyebrow={t.testi.eyebrow} title={t.testi.title} sub={t.testi.sub} />
          <div className="flex items-center gap-4">
            <button onClick={() => go(-1)} data-testid="testimonial-prev" aria-label="Previous" className="grid h-11 w-11 place-items-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10"><ChevronLeft size={18} /></button>
            <button onClick={() => go(1)} data-testid="testimonial-next" aria-label="Next" className="grid h-11 w-11 place-items-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10"><ChevronRight size={18} /></button>
            <div className="ml-2 flex gap-2">
              {items.map((_, k) => (
                <button key={k} onClick={() => setI(k)} data-testid={`testimonial-dot-${k}`} aria-label={`Review ${k + 1}`}
                  className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${k === i ? "w-8 bg-[#D4AF37]" : "w-3 bg-zinc-700"}`} />
              ))}
            </div>
          </div>
        </div>
        <div className="glass-card mx-auto mb-16 w-full max-w-md overflow-hidden md:mb-0" data-testid="testimonial-carousel"
          onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="flex items-center gap-3 border-b border-[#D4AF37]/20 bg-black/40 px-5 py-4">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-[#F3D77A] to-[#9A7B2C] font-serif font-bold text-black">D</div>
            <div>
              <p className="text-sm font-semibold text-white">{t.testi.desk}</p>
              <p className="flex items-center gap-1.5 text-xs text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />{t.testi.online}</p>
            </div>
          </div>
          <div key={`${i}-${t.testi.desk}`} className="flex min-h-[320px] flex-col gap-4 p-5" data-testid={`testimonial-slide-${i}`}>
            <div className="chat-in flex items-center gap-2" style={{ animationDelay: "0ms" }}>
              <div className="grid h-8 w-8 place-items-center rounded-full border border-[#D4AF37]/40 text-xs font-bold text-[#D4AF37]">{cur.name[0]}</div>
              <span className="text-xs text-zinc-400" data-testid="testimonial-name">{cur.name} · {cur.city}</span>
              <span className="flex text-[#D4AF37]">{[...Array(5)].map((_, s) => <Star key={s} size={11} fill="currentColor" />)}</span>
            </div>
            <Bubble side="in" delay="150ms">{cur.msg}</Bubble>
            <div className="typing chat-in flex items-center gap-2 text-xs text-zinc-500" style={{ animationDelay: "700ms" }}>
              <span className="dots"><i /><i /><i /></span>{t.testi.typing}
            </div>
            <Bubble side="out" delay="1700ms">
              {cur.reply}
              <span className="mt-1 flex justify-end"><CheckCheck size={14} /></span>
            </Bubble>
          </div>
        </div>
      </div>
    </section>
  );
};
